import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaRocket, FaUsers, FaCode, FaBullseye, FaEye, 
  FaHandshake, FaChartLine, FaCogs, FaShieldAlt, FaAward,
  FaCheckCircle, FaArrowRight, FaBrain, FaCloud, FaGlobe
} from 'react-icons/fa';

const About = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  // Updated journey with realistic years (no future dates)
  const journey = [
    {
      year: '2026',
      title: 'Foundation',
      description: 'Sronzer Technologies founded by Nishant Mishra, Prajwal Singh & Abhishek Singh with a vision to revolutionize CRM solutions.',
      icon: FaRocket,
    },
    {
      year: '2026',
      title: 'Salesforce Ecosystem Growth',
      description: 'Achieved Salesforce Partner status and built a team of 25+ certified CRM experts serving global clients.',
      icon: FaHandshake,
    },
    {
      year: '2026',
      title: 'Global Expansion & AI Integration',
      description: 'Expanded operations across APAC, launched AI-powered analytics modules for enterprise CRM.',
      icon: FaGlobe,
    },
  ];

  const teamStats = [
    { value: '25+', label: 'Expert Developers' },
    { value: '50+', label: 'Successful Projects' },
    { value: '98%', label: 'Client Retention' },
    { value: '24/7', label: 'Support Available' },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative py-24 bg-primary-950 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-5" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <span className="text-accent font-display text-sm tracking-[0.3em]">OUR STORY</span>
            <h1 className="text-4xl md:text-6xl font-display font-bold text-white mt-4 mb-6">
              Empowering Businesses Through <span className="text-accent">Smart CRM</span>
            </h1>
            <div className="golden-line mx-auto" />
          </motion.div>
        </div>
      </section>

      <section className="relative py-20 bg-primary-900" ref={ref}>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
                From <span className="text-accent">Startup</span> to Trusted CRM Partner
              </h2>
              <div className="golden-line mb-6" />
              
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Founded in 2026, <span className="text-accent font-semibold">Sronzer Technologies</span> emerged 
                  as a purpose-driven company dedicated to helping businesses master customer relationships through 
                  innovative CRM solutions.
                </p>
                <p>
                  Our team of <span className="text-accent font-semibold">25+ expert developers and CRM specialists</span> 
                  is capable of delivering anything our clients ask for. From Salesforce implementation to custom 
                  CRM development, we've got you covered.
                </p>
                <p>
                  We partner with industry leaders like Salesforce and integrate with 500+ tools to provide 
                  seamless, end-to-end solutions that transform how businesses operate.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-8">
                {teamStats.map((stat, idx) => (
                  <div key={idx} className="text-center p-4 glass-card">
                    <div className="text-2xl font-display font-bold text-accent">{stat.value}</div>
                    <div className="text-xs text-gray-400 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="glass-card overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Team Collaboration"
                  className="w-full h-96 object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -bottom-6 -left-6 glass-card p-6 backdrop-blur-xl"
              >
                <FaRocket className="text-3xl text-accent mb-2" />
                <div className="text-sm text-gray-300">Innovation</div>
                <div className="text-lg font-display font-bold text-white">Driven Team</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="relative py-20 bg-primary-950">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-accent font-display text-sm tracking-[0.3em]">TIMELINE</span>
            <h2 className="section-title mt-4">Our <span className="text-accent">Journey</span></h2>
            <div className="golden-line mx-auto mt-6" />
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 justify-center">
            {journey.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card p-6 text-center group hover:border-accent/30 transition-all"
              >
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-accent/20 transition-all">
                  <item.icon className="text-2xl text-accent" />
                </div>
                <span className="text-accent font-display text-sm tracking-wider">{item.year}</span>
                <h3 className="text-lg font-display font-bold text-white mt-2 mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 bg-primary-900">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-accent font-display text-sm tracking-[0.3em]">OUR PHILOSOPHY</span>
            <h2 className="section-title mt-4">Why Choose <span className="text-accent">Sronzer</span></h2>
            <div className="golden-line mx-auto mt-6" />
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FaCode,
                title: 'Expert Development Team',
                description: 'Our developers are capable of delivering anything you ask for, with expertise in Salesforce, custom CRM, and enterprise integrations.'
              },
              {
                icon: FaShieldAlt,
                title: 'Enterprise Security',
                description: 'Bank-grade encryption, SOC2 compliance and processes for complete data protection.'
              },
              {
                icon: FaHandshake,
                title: 'Partnership Approach',
                description: 'We don\'t just implement solutions; we become your long-term technology partner for growth.'
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="glass-card p-8 text-center"
              >
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="text-2xl text-accent" />
                </div>
                <h3 className="text-xl font-display font-bold text-white mb-4">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-12 glass-card p-8 text-center"
          >
            <FaEye className="text-4xl text-accent mx-auto mb-4" />
            <h2 className="text-2xl font-display font-bold text-white mb-4">Our Vision</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              To be the most trusted CRM solutions provider, empowering businesses worldwide 
              to build meaningful customer relationships through innovative technology and exceptional service.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;